import Table from "react-bootstrap/Table";
import Pagination from "react-bootstrap/Pagination";
import moment from "moment";
import { Copy } from "lucide-react";
import { toast } from "react-toastify";
import { CDN_CONTAINER, CDN_ENDPOINT } from "../../../config";

const PRODUCT_BADGE = {
  insurance: { label: "Insurance Project", className: "product-badge-insurance" },
  skor_protect: { label: "Skor Protect", className: "product-badge-skor-protect" },
};

// raw_status values are product-specific: insurance -> failed | contracted | awarded | confirmed,
// skor_protect -> waiting_for_payment | waiting_for_rental_contract
// "confirmed" is treated as a synonym of "awarded" (same label, same green badge)
const INSURANCE_STATUS_LABELS = {
  insurance: {
    failed: "Failed",
    contracted: "Contracted",
    awarded: "Awarded",
    confirmed: "Awarded",
  },
  skor_protect: {
    waiting_for_payment: "Awaiting for payment",
    waiting_for_rental_contract: "Waiting for rental contract",
  },
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

const getInsuranceStatusLabel = (item) => {
  const label = INSURANCE_STATUS_LABELS[item.product]?.[item.raw_status];
  if (label) return label;
  if (!item.raw_status) return "N/A";
  return item.raw_status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getInsuranceStatusBadgeClass = (item) => {
  if (item.product === "insurance") {
    if (item.raw_status === "awarded" || item.raw_status === "confirmed") return "status-badge-active";
    if (item.raw_status === "failed") return "status-badge-deleted";
    if (item.raw_status === "contracted") return "insurance-payment-badge direct-debit";
  }
  if (item.product === "skor_protect") {
    if (item.raw_status === "waiting_for_payment") return "status-badge-expired";
    if (item.raw_status === "waiting_for_rental_contract") return "insurance-payment-badge direct-debit";
  }
  return "insurance-payment-badge";
};

const InsuranceProtectTable = ({ data, total, currentPage, limit, onPageChange }) => {
  const totalPages = Math.ceil(total / limit);
  const indexOfFirstItem = (currentPage - 1) * limit + 1;
  const indexOfLastItem = Math.min(currentPage * limit, total);

  const formatProperty = (property) => {
    if (!property) return "N/A";
    return (
      [property.street, property.street_number, property.muncipality]
        .filter(Boolean)
        .join(", ") || "N/A"
    );
  };

  const handleCopyPropertyId = async (propertyId) => {
    if (!propertyId) return;
    try {
      await navigator.clipboard.writeText(propertyId);
      toast.success("Property ID copied");
    } catch {
      toast.error("Failed to copy property ID");
    }
  };

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First key="first" onClick={() => onPageChange(1)} disabled={currentPage === 1} />
    );
    items.push(
      <Pagination.Prev key="prev" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
    );
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
          {page}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{total > 0 ? indexOfFirstItem : 0}</strong> to{" "}
          <strong>{indexOfLastItem}</strong> of <strong>{total}</strong> records
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <>
      <div className="table-responsive ip-table-scroll">
        <Table hover className="landlord-modal-table insurance-protect-table mb-0">
          <thead>
            <tr>
              <th style={{ width: "32px" }}>#</th>
              <th className="ip-product-col">PRODUCT</th>
              <th className="ip-property-col">PROPERTY</th>
              <th className="ip-tenants-col">TENANTS</th>
              <th className="ip-workspace-col">WORKSPACE</th>
              <th className="ip-beneficiary-col">BENEFICIARY</th>
              <th className="ip-policy-date-col">POLICY DATE</th>
              <th className="ip-premium-col">PREMIUM</th>
              <th className="ip-payment-status-col">PAYMENT STATUS</th>
              <th className="ip-status-col">INSURANCE STATUS</th>
              <th className="ip-compact-col">POLICY DOC</th>
              <th className="ip-compact-col">INVOICE DOC</th>
              <th className="d-none">PROPERTY ID</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const product = PRODUCT_BADGE[item.product] || {};
                const tenantNames = (item.tenants || [])
                  .map((t) => `${t.fname || ""} ${t.lname || ""}`.trim())
                  .filter(Boolean);
                const isPaid = item.payment_status === "paid";

                return (
                  <tr key={item._id || index}>
                    <td className="text-muted" style={{ width: "32px" }}>
                      {indexOfFirstItem + index}
                    </td>
                    <td className="ip-product-col">
                      <span className={`product-badge ${product.className || ""}`}>
                        {product.label || "N/A"}
                      </span>
                    </td>
                    <td
                      className="fw-600 ip-property-col"
                      style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}
                    >
                      {formatProperty(item.property)}
                    </td>
                    <td className="ip-tenants-col">
                      <div className="fw-600" style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}>
                        {tenantNames.length > 0 ? tenantNames.join(", ") : "N/A"}
                      </div>
                    </td>
                    <td
                      className="fw-600 ip-workspace-col"
                      style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}
                    >
                      {item.workspace?.name || "N/A"}
                    </td>
                    <td className="ip-beneficiary-col">
                      <div className="fw-600">{capitalize(item.beneficiary?.type) || "N/A"}</div>
                      {item.beneficiary?.name && (
                        <div className="text-muted small">{item.beneficiary.name}</div>
                      )}
                    </td>
                    <td className="text-muted small fw-medium ip-policy-date-col">
                      {item.policy_date ? moment(item.policy_date).format("DD MMM YYYY") : "N/A"}
                    </td>
                    <td className="fw-600 ip-premium-col">
                      {item.premium != null ? `€ ${Number(item.premium).toLocaleString()}` : "N/A"}
                    </td>
                    <td className="ip-payment-status-col">
                      <span className={isPaid ? "status-badge-active" : "status-badge-not-paid"}>
                        {isPaid ? "Paid" : "Not paid"}
                      </span>
                    </td>
                    <td className="ip-status-col">
                      <span className={getInsuranceStatusBadgeClass(item)}>
                        {getInsuranceStatusLabel(item)}
                      </span>
                    </td>
                    <td className="ip-compact-col">
                      {item.policy_doc ? (
                        <a
                          href={`${CDN_ENDPOINT}/${CDN_CONTAINER}/${item.policy_doc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="insurance-doc-link"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="ip-compact-col">
                      {item.invoice_doc ? (
                        <a
                          href={`${CDN_ENDPOINT}/${CDN_CONTAINER}/${item.invoice_doc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="insurance-doc-link"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {item.property?._id && (
                        <button
                          type="button"
                          className="ip-copy-btn"
                          title="Copy property ID"
                          onClick={() => handleCopyPropertyId(item.property._id)}
                        >
                          <Copy size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={13} className="text-center py-5 text-muted">
                  No results found matching your filters
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {totalPages > 1 && total > 0 && <div className="px-1">{renderPagination()}</div>}
    </>
  );
};

export default InsuranceProtectTable;
